import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ExceptionManager } from "Core/logManager";
import { clientQuery, clientMutation } from "Core/data/GraphQLApi";
import { endpoints } from "Core/defaultValues";
import {uploadFile} from "Core/data/FileUpload";

const INIT_STATE = {
  //createDriver
  driverResponse: null,
  loading: false,
  error: null,

  //searchDrivers
  entity: { id: "drivers" },
  loading: false,
  total: 0,
  filters: {
    includeInactiveDrivers: true,
    unassigned: true,
    openSearch: "",
    tagIds: [],
    limit: 0,
    offset: 0,
  },
  registers: [],
  driverInfo: {},
  errorSearch: null,
  openFilters: false,

  //openCreateEdit
  openCreateEdit: false,
  driverGroup: null,
  supervisor: [],
  loadingSupervisor: false,
  errorSupervisor: null,

  //searchEntityLists
  entityCountryList: [],
  entityStateList: [],
  entityHosRuleSetList: [],
  loadingEntities: false,
  errorEntities: null,

  driverAvatar: null,

  //openDriverTransactionModal
  openTransaction: false,
  transactionError: false,
  transactions: [],
  fileUrl: null,
};

const route =
  "modules/driver/views/driverTable/components/createEditDriver/redux/slices";

export const createDriver = createAsyncThunk(
  `${route}/createDriver`,
  async (payload, { rejectWithValue, dispatch }) => {
    const {
      name,
      externalId,
      groupId,
      tagIds,
      timeZoneId,
      unitId,
      username,
      password,
      defaultTab,
      avatarFileId,
      status,
      supervisorId,
      licenseNumber,
      licenseCountry,
      licenseState,
      driverPin,
      hosRulesetIds,
      hosExempt,
      email,
      phone,
      notifyByEmail,
      notifyBySms,
      contactInfo = null,
      hosExemptComment = null,
      keyFobHex = null,
    } = payload;

    try {
      let data = await clientMutation(
        `
        mutation createDriver (
            $name: String!,
            $externalId: String,
            $tagIds: [Int], 
            $timeZoneId: Int!, 
            $contactInfo: String, 
            $unitId: Int, 
            $username: String, 
            $password: String, 
            $defaultTab: String,
            $avatarFileId: Int,
            $defaultTab: String,
            $driverTransitionFrom: String,
            $status: Int
            $supervisorId: Int
            $licenseNumber: String
            $licenseCountry: String 
            $licenseState: String
            $driverPin: String
            $hosRulesetIds: String
            $hosExempt: Boolean
            $email: String
            $phone: String
            $notifyByEmail: Boolean
            $notifyBySms: Boolean
            $contactInfo: String
            $hosExemptComment: String
            $keyFobHex: String
        ) {
            createDriver (
                name: $name, 
                externalId: $externalId, 
                tagIds: $tagIds,
                timeZoneId: $timeZoneId, 
                unitId: $unitId, 
                username: $username, 
                password: $password, 
                defaultTab: $defaultTab,
                avatarFileId: $avatarFileId,
                driverTransitionFrom: $driverTransitionFrom
                status: $status
                supervisorId: $supervisorId
                licenseNumber: $licenseNumber
                licenseCountry: $licenseCountry
                licenseState: $licenseState
                driverPin: $driverPin
                hosRulesetIds: $hosRulesetIds
                hosExempt: $hosExempt
                email: $email
                phone: $phone
                notifyByEmail: $notifyByEmail
                notifyBySms: $notifyBySms
                contactInfo: $contactInfo
                hosExemptComment: $hosExemptComment
                keyFobHex: $keyFobHex
            ) {
                externalId
                companyId
                driverId
                tags {
                    id
                    label
                    color
                    path
                }
                unit {
                    id
                    label
                }
                timeZone {
                    id
                    title
                }
                fsmDriver{
                    username
                    defaultTab
                }
                licenseNumber
                licenseState
                driverPin
                hosRulesetIds
                hosExempt
                email
                phone
                notifyByEmail
                notifyBySms
                hosExemptComment
            }
        }
        `,
        {
          name,
          externalId: externalId || "",
          groupId,
          tagIds,
          timeZoneId,
          unitId,
          username,
          password,
          defaultTab,
          avatarFileId,
          driverTransitionFrom: "CloudWebSite",
          status,
          supervisorId: supervisorId || 0,
          licenseNumber,
          licenseCountry,
          licenseState,
          driverPin,
          hosRulesetIds,
          hosExempt,
          email,
          phone,
          notifyByEmail,
          notifyBySms,
          contactInfo,
          hosExemptComment,
          keyFobHex,
        },
        endpoints.GRAPHQL_GENERAL_V2
      );

      if (data) {
        return data?.createDriver;
      } else {
        throw "Error while creating driver";
      }
    } catch (exc) {
      ExceptionManager(exc, route, "createDriver");
      return rejectWithValue(exc);
    }
  }
);

export const updateDriver = createAsyncThunk(
  `${route}/updateDriver`,
  async (payload, { rejectWithValue, dispatch }) => {
    const {
      id,
      name,
      externalId,
      tagIds,
      timeZoneId,
      contactInfo,
      unitId,
      username,
      password,
      defaultTab,
      avatarFileId,
      tempAvatarUrl,
      status,
      supervisorId,
      licenseNumber,
      licenseCountry,
      licenseState,
      driverPin,
      hosRulesetIds,
      hosExempt,
      email,
      phone,
      notifyByEmail,
      notifyBySms,
      hosExemptComment,
      keyFobHex,
    } = payload;

    try {
      let data = await clientMutation(
        `
        mutation updateDriver (
            $id: Int!, 
            $name: String, 
            $externalId: String, 
            $tagIds: [Int],
            $timeZoneId: Int, 
            $contactInfo: String, 
            $unitId: Int, 
            $username: String, 
            $password: String, 
            $defaultTab: String,
            $avatarFileId: Int
            $tempAvatarUrl: String
            $driverTransitionFrom: String
            $status: Int
            $supervisorId: Int
            $licenseNumber: String
            $licenseCountry: String
            $licenseState: String
            $driverPin: String
            $hosRulesetIds: String
            $hosExempt: Boolean
            $hosExemptComment: String
            $email: String
            $phone: String
            $notifyByEmail: Boolean
            $notifyBySms: Boolean
            $keyFobHex: String
        ) {
            updateDriver (
                id: $id, 
                name: $name, 
                externalId: $externalId, 
                tagIds: $tagIds,
                timeZoneId: $timeZoneId, 
                contactInfo: $contactInfo, 
                unitId: $unitId, 
                username: $username, 
                password: $password, 
                defaultTab: $defaultTab,
                avatarFileId: $avatarFileId
                tempAvatarUrl: $tempAvatarUrl
                driverTransitionFrom: $driverTransitionFrom
                status: $status
                supervisorId: $supervisorId
                licenseNumber: $licenseNumber
                licenseCountry: $licenseCountry
                licenseState: $licenseState
                driverPin: $driverPin
                hosRulesetIds: $hosRulesetIds
                hosExempt: $hosExempt
                email: $email
                phone: $phone
                notifyByEmail: $notifyByEmail
                notifyBySms: $notifyBySms
                hosExemptComment: $hosExemptComment
                keyFobHex: $keyFobHex
            ) {
                name
                externalId
                contactInfo
                companyId
                driverId
                tags {
                    id
                    label
                    color
                    path
                }
                unit {
                    id
                    label
                }
                timeZone {
                    id
                    title
                }
                fsmDriver{
                    username
                    defaultTab
                }
                licenseNumber
                licenseCountry
                licenseState
                driverPin
                hosRulesetIds
                hosExempt
                hosExemptComment
                email
                phone
                notifyByEmail
                notifyBySms
            }
        }
    `,
        {
          id,
          name,
          externalId: externalId || "",
          tagIds,
          timeZoneId,
          contactInfo,
          unitId,
          username,
          password,
          defaultTab,
          avatarFileId,
          tempAvatarUrl,
          driverTransitionFrom: "CloudWebSite",
          status,
          supervisorId,
          licenseNumber,
          licenseCountry,
          licenseState,
          driverPin,
          hosRulesetIds,
          hosExempt,
          email,
          phone,
          notifyByEmail,
          notifyBySms,
          hosExemptComment,
          keyFobHex,
        },
        endpoints.GRAPHQL_GENERAL_V2
      );

      if (data) {
        return data?.updateDriver;
      } else {
        throw "Error while updating driver";
      }
    } catch (exc) {
      ExceptionManager(exc, route, "updateDriver");
      return rejectWithValue(exc);
    }
  }
);

export const searchEntityLists = createAsyncThunk(
  `${route}/searchEntityLists`,
  async (payload, { rejectWithValue, dispatch }) => {
    const { terms, entityName } = payload;

    const body = {
      conditions: [
        {
          fields: ["entityName"],
          terms: terms,
        },
      ],
      limit: 100,
      offset: 0,
    };

    try {
      let data = await clientQuery(
        `query searchEntityLists ($conditions: [EntityListSearchInput], $sort: [EntityListSortInput], $limit: Int, $offset: Int) {
    searchEntityLists (conditions: $conditions, sort: $sort, limit: $limit, offset: $offset) {
        items {
            entityId
            entityName
            parentId
            label
            value1
            value2
        }
        total
    }
}`,
        body,
        endpoints.GRAPHQL_GENERAL_V2
      );

      if (data) {
        const {
          searchEntityLists: { items, total },
        } = data;
        return { items, total, entityName };
      } else {
        throw "Error while getting entities";
      }
    } catch (exc) {
      ExceptionManager(exc, route, "searchEntityLists");
      return rejectWithValue(exc);
    }
  }
);

export const createDriverAvatar = createAsyncThunk(
  `${route}/createDriverAvatar`,
  async (payload, { rejectWithValue }) => {
    const { file, fileKeyDelete } = payload;

    try {
      if (fileKeyDelete) {
        const mutation = `mutation deleteAttachment ($id: Int!) {deleteAttachment (id: $id)}`;
        await clientMutation(mutation, { id: fileKeyDelete }, endpoints.GRAPHQL_GENERAL_V2);
      }

      if (file) {
        let extension = file?.name.split(".").pop();
        let presignedUrlResponse = await clientQuery(
          `query(
            $repositoryCode: String
            $fileExtension: String
          ) {
            Data: getPresignedUrl(
              repositoryCode: $repositoryCode,
              fileExtension: $fileExtension
            ) {
              repositoryCode
              fileKey
              url
            }
          }`,
          {
            repositoryCode: endpoints.REPOSITORY_CODE,
            fileExtension: extension,
          },
          endpoints.GRAPHQL_GENERAL_V2
        );

        if (!presignedUrlResponse || !presignedUrlResponse.Data) {
          throw "There are a problem uploading the File";
        }

        await uploadFile(presignedUrlResponse.Data.url, file);

        let registerAttachmentResult = await clientMutation(
          `mutation ($repositoryCode: String!, $fileKey: String!, $originalName: String){
            attachment: registerAttachment(repositoryCode: $repositoryCode, fileKey: $fileKey, originalName: $originalName){
              id
              repositoryId
              repositoryType
              repositoryCode
              repositoryPath
              fileKey
              originalName
            }
          }`,
          {
            repositoryCode: presignedUrlResponse.Data.repositoryCode,
            fileKey: presignedUrlResponse.Data.fileKey,
            originalName: file?.name,
          },
          endpoints.GRAPHQL_GENERAL
        );

        if (registerAttachmentResult) {
          return registerAttachmentResult.attachment;
        } else {
          throw "There are a problem register the File";
        }
      } else {
        return { id: null, fileKey: fileKeyDelete };
      }
    } catch (exc) {
      ExceptionManager(
        exc,
        "modules/settings/views/driver/redux/sagas",
        "createDriverAvatar",
        true
      );
      return rejectWithValue(exc);
    }
  }
);

export const getUrlFileAttachment = createAsyncThunk(
  `${route}/getUrlFileAttachment`,
  async (payload, { rejectWithValue }) => {
    const { id } = payload;
    try {
      const data = await clientQuery(
        `query (
          $id: Int!
        ){
          downloadAttachmentUrl: downloadAttachmentUrl(
            id: $id
          )
        }`,
        {
          id
        },
        endpoints.GRAPHQL_GENERAL,
      );

      if (data && data.downloadAttachmentUrl) {
        return data.downloadAttachmentUrl;
      } else {
        throw 'There are not url attachment';
      }
    } catch (exc) {
      ExceptionManager(
        exc,
        'modules/settings/views/driver/redux/sagas',
        'getUrlFileAttachment',
      );
      return rejectWithValue(exc);
    }
  }
);

export const searchDriverTransaction = createAsyncThunk(
  `${route}/searchDriverTransaction`,
  async (payload, { rejectWithValue }) => {
    const {
      transactionId,
      nextToken
    } = payload;

    try {
      const query = `
        query searchTransactions ($transactionId: String!, $nextToken: String) {
          searchTransactions (transactionId: $transactionId, nextToken: $nextToken) {
            items {
              Id
              TransactionId
              Type
              State
              ErrorMessage
              NewId
              Data
              CreatedBy
              CreatedOn
              ProcessedOn
            }
            nextToken
          }
        }
      `;

      const response = await clientQuery(
        query,
        {
          transactionId,
          nextToken
        },
        endpoints.GRAPHQL_GENERAL
      );

      if (!response || !response.searchTransactions) {
        throw "Error while getting Transactions";
      }

      return { items: response.searchTransactions, error: false };
    } catch (exc) {
      ExceptionManager(
        exc,
        "modules/settings/views/driver/redux/sagas",
        "searchDriverTransaction"
      );
      return rejectWithValue({ items: [], error: true });
    }
  }
);

export const driverCreateEditSliceRedux = createSlice({
  name: `${route}/driverCreateEdit`,
  initialState: INIT_STATE,
  reducers: {
    setEntityName: (state, action) => {
      state.entityName = action.payload;
    },
    openDriverTransaction: (state, action) => {
      return {
        ...state,
        openTransaction: action.payload?.open
      };
    },
    openDriverCreateEditModal: (state, action) => {
      return {
        ...state,
        openCreateEdit: action.payload?.open,
        driverGroup: action.payload?.driverGroup,
        driverResponse: null,
      };
    },
    clearReduxCreateEditDriver: (state, action) => {
      return {
        ...state,
        driverResponse: null,
        error: null,
      };
    },
  },
  extraReducers: (builder) => {
    //createDriver
    builder.addCase(createDriver.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(createDriver.fulfilled, (state, { payload }) => {
      if(!payload){
        state.error = true;
      }
      state.loading = false;
      state.driverResponse = payload;
    });
    builder.addCase(createDriver.rejected, (state, { payload }) => {
      state.loading = false;

      if(payload.error === "The username for the driver is duplicated."){
        state.error = 'usernameError';
      } else if(payload.error === "Driver license number duplicated"){
        state.error = 'licenseNumberError';
      } else if(payload.error === "Key Fob duplicated"){
        state.error = 'keyFobHexError';
      } else if(payload.error === 'The username must contain between 4 and 12 characters'){
        state.error = 'usernameErrorLength';
      } else if(payload.error === "email_already_exists"){
        state.error = 'email_already_exists';
      }
      else {
        state.error = payload.error;
      }
    });

    //updateDriver
    builder.addCase(updateDriver.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(updateDriver.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.driverResponse = payload;
    });
    builder.addCase(updateDriver.rejected, (state, { payload }) => {
      if(payload.error === "It is not possible to inactivate the driver because it has active work orders assigned to it"){
        state.error = 'workOrderError';
      } else if(payload.error === "Driver license number duplicated"){
        state.error = 'licenseNumberError';
      } else {
        state.error = payload.error;
      }
      state.loading = false;
    });

    //searchEntityLists
    builder.addCase(searchEntityLists.pending, (state, action) => {
      state.loadingEntities = true;
    });
    builder.addCase(searchEntityLists.fulfilled, (state, { payload }) => {
      state.loadingEntities = false;

      let contries = payload.items.filter(item => item.entityName === 'COUNTRY');
      let states = payload.items.filter(item => item.entityName === 'STATE');
      let hosRuleSet = payload.items.filter(item => item.entityName === 'hosRuleSet');

      state.entityCountryList = contries;
      state.entityStateList = states;
      state.entityHosRuleSetList = hosRuleSet;
      state.totalEntities = payload.total;
    });
    builder.addCase(searchEntityLists.rejected, (state, { payload }) => {
      state.loadingEntities = false;
      state.errorEntities = payload;
    });

    //createDriverAvatar
    builder.addCase(createDriverAvatar.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createDriverAvatar.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.driverAvatar = payload;
    });
    builder.addCase(createDriverAvatar.rejected, (state, { payload }) => {
      state.loading = false;
      state.error = payload;
    });

    //getUrlFileAttachment
    builder.addCase(getUrlFileAttachment.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getUrlFileAttachment.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.fileUrl = payload;
    });
    builder.addCase(getUrlFileAttachment.rejected, (state, { payload }) => {
      state.loading = false;
      state.error = payload;
    });

    //searchDriverTransaction
    builder.addCase(searchDriverTransaction.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(searchDriverTransaction.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.transactions = payload.items;
      state.transactionError = payload.error;
    });
    builder.addCase(searchDriverTransaction.rejected, (state, { payload }) => {
      state.loading = false;
      state.transactions = [];
      state.transactionError = true;
    });
  },
});

const { actions, reducer } = driverCreateEditSliceRedux;

export const { openDriverCreateEditModal, setEntityName, openDriverTransaction, clearReduxCreateEditDriver } = actions;

export default reducer;
